"use client";

import { FileText } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { Skeleton } from "@/components/ui/skeleton";
import { useFetchMyStatements } from "@/hooks/statement/useFetchMyStatements";
import { MyStatementCard } from "./MyStatementCard";

export const MyStatements: React.FC = () => {
  const t = useTranslations("statement");
  const query = useFetchMyStatements();
  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && !query.isFetchingNextPage && query.hasNextPage) {
      query.fetchNextPage();
    }
  }, [
    inView,
    query.isFetchingNextPage,
    query.hasNextPage,
    query.fetchNextPage,
  ]);

  const items = query.data?.pages.flatMap((p) => p?.data?.result ?? []) ?? [];

  if (query.isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="default" className="rounded-xl h-24" />
        ))}
      </div>
    );
  }

  if (query.error) {
    return (
      <p className="text-center text-sm text-semantic-error py-8">
        {t("mine.error")}
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center text-center py-12 space-y-4">
        <FileText className="size-16 text-text-secondary/40" />
        <p className="text-sm text-text-secondary">{t("mine.empty")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item) => (
        <MyStatementCard key={item.id} statement={item} />
      ))}
      <div ref={ref} className="py-4">
        {query.isFetchingNextPage && (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <Skeleton key={i} variant="default" className="rounded-xl h-24" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
